import { Box, Table, Thead, Tbody, Tr, Th, Td, Text, Flex, Skeleton, Icon, Tooltip } from "@chakra-ui/react";
import {
    flexRender,
    getCoreRowModel,
    useReactTable,
    type ColumnFiltersState,
    type RowSelectionState,
    type SortingState,
    type Updater,
} from "@tanstack/react-table";
import { useMemo } from "react";
import { IconChevronUp, IconChevronDown, IconSelector, IconFilterFilled } from "@tabler/icons-react";
import type { EntityCandidate } from "../../api/models/EntityCandidate";
import { candidatesColumns } from "./candidatesColumns";
import { describeColumnFilter } from "./filterDescriptions";

interface CandidatesTableProps {
    data: EntityCandidate[];
    totalCount: number;
    isLoading: boolean;
    isFetching: boolean;
    sorting: SortingState;
    columnFilters: ColumnFiltersState;
    rowSelection: RowSelectionState;
    onSortingChange: (sorting: SortingState) => void;
    onColumnFiltersChange: (filters: ColumnFiltersState) => void;
    onRowSelectionChange: (updaterOrValue: Updater<RowSelectionState>) => void;
    onRowClick: (candidate: EntityCandidate) => void;
    selectedCandidateId: string | undefined;
}

export function CandidatesTable({
    data,
    isLoading,
    isFetching,
    sorting,
    columnFilters,
    rowSelection,
    onSortingChange,
    onRowSelectionChange,
    onRowClick,
    selectedCandidateId,
}: CandidatesTableProps) {
    const handleSortingChange = (updaterOrValue: Updater<SortingState>) => {
        const newSorting = typeof updaterOrValue === "function" ? updaterOrValue(sorting) : updaterOrValue;
        onSortingChange(newSorting);
    };

    // Map of column id -> humanized description for columns with an active filter, used to show
    // a filter indicator + tooltip on the grid header so users can see what's constraining the list.
    const activeFilterDescriptionByColumnId = useMemo(() => {
        const map = new Map<string, string>();
        for (const filter of columnFilters) {
            const desc = describeColumnFilter(filter, columnFilters);
            if (desc) map.set(filter.id, `${desc.label}: ${desc.value}`);
        }
        return map;
    }, [columnFilters]);

    const table = useReactTable<EntityCandidate>({
        data,
        columns: candidatesColumns,
        state: { sorting, rowSelection },
        onSortingChange: handleSortingChange,
        onRowSelectionChange,
        getCoreRowModel: getCoreRowModel(),
        getRowId: row => row.id,
        enableRowSelection: true,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
    });

    return (
        <Box overflowX="auto" position="relative">
            {isFetching && !isLoading && (
                <Box position="absolute" top={0} left={0} right={0} height="2px" bg="blue.400" zIndex={1} animation="pulse 1.5s ease-in-out infinite" />
            )}
            <Table variant="simple" size="sm">
                <Thead bg="gray.50" _dark={{ bg: "gray.700" }}>
                    {table.getHeaderGroups().map(headerGroup => (
                        <Tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => {
                                const sorted = header.column.getIsSorted();
                                const canSort = header.column.getCanSort();
                                const filterDescription = activeFilterDescriptionByColumnId.get(header.column.id);
                                const isFiltered = filterDescription !== undefined;
                                return (
                                    <Th
                                        key={header.id}
                                        width={`${header.getSize()}px`}
                                        cursor={canSort ? "pointer" : "default"}
                                        userSelect="none"
                                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                                        whiteSpace="nowrap"
                                        bg={isFiltered ? "blue.50" : undefined}
                                        _dark={isFiltered ? { bg: "blue.900" } : undefined}
                                        _hover={canSort ? { bg: isFiltered ? "blue.100" : "gray.100", _dark: { bg: "gray.600" } } : undefined}
                                    >
                                        <Flex align="center" gap={1}>
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            {isFiltered && (
                                                <Tooltip label={filterDescription} hasArrow>
                                                    <Flex onClick={e => e.stopPropagation()} align="center">
                                                        <Icon as={IconFilterFilled} boxSize={3} color="blue.500" aria-label="Filter applied" />
                                                    </Flex>
                                                </Tooltip>
                                            )}
                                            {canSort && (
                                                <Icon
                                                    as={sorted === "asc" ? IconChevronUp : sorted === "desc" ? IconChevronDown : IconSelector}
                                                    boxSize={3}
                                                    color={sorted ? "blue.500" : "gray.400"}
                                                />
                                            )}
                                        </Flex>
                                    </Th>
                                );
                            })}
                        </Tr>
                    ))}
                </Thead>
                <Tbody>
                    {isLoading
                        ? Array.from({ length: 10 }).map((_, i) => (
                              <Tr key={i}>
                                  {candidatesColumns.map((_, ci) => (
                                      <Td key={ci}>
                                          <Skeleton height="16px" />
                                      </Td>
                                  ))}
                              </Tr>
                          ))
                        : table.getRowModel().rows.map(row => (
                              <Tr
                                  key={row.id}
                                  cursor="pointer"
                                  bg={row.original.id === selectedCandidateId ? "blue.50" : undefined}
                                  _dark={{ bg: row.original.id === selectedCandidateId ? "blue.900" : undefined }}
                                  _hover={{ bg: "gray.50", _dark: { bg: "gray.700" } }}
                                  onClick={() => onRowClick(row.original)}
                              >
                                  {row.getVisibleCells().map(cell => (
                                      <Td key={cell.id} py={2}>
                                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                      </Td>
                                  ))}
                              </Tr>
                          ))}
                    {!isLoading && data.length === 0 && (
                        <Tr>
                            <Td colSpan={candidatesColumns.length} textAlign="center" py={8}>
                                <Text color="gray.500">No candidates found.</Text>
                            </Td>
                        </Tr>
                    )}
                </Tbody>
            </Table>
        </Box>
    );
}
